/**
 * ceramic-offer.js (Impulse / cart drawer)
 *
 * Adds a ceramic upgrade product automatically when:
 * - cart.attributes.ceramic_offer === "1"
 * - AND a wheel product is in cart (line item property "_is_wheel" or "is_wheel" === "1")
 * - AND the upgrade is not already in cart
 *
 * Then:
 * - Adds the upgrade
 * - Triggers Impulse to rebuild the cart drawer (cart:build)
 * - Marks cart.attributes.ceramic_offer = "applied" (so it won't add twice)
 */

(function () {
  // TODO: Replace with your ceramic upgrade *variant* ID (NOT product ID).
  const UPGRADE_VARIANT_ID = 56705947042175;

  // One-per-order behaviour: once applied, do nothing further.
  const STOP_AFTER_APPLIED = true;

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  async function updateCartAttributes(attrs) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });

    const data = await res.json();
    if (!res.ok) {
      console.error('[ceramic-offer] Failed to update cart attributes:', data);
      return { ok: false, data };
    }
    return { ok: true, data };
  }

  async function addUpgradeVariant(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });

    const data = await res.json();

    if (!res.ok) {
      console.error('[ceramic-offer] Failed to add upgrade variant:', data);
      return { ok: false, data };
    }

    return { ok: true, data };
  }

  function getOfferState(cart) {
    const attrs = cart.attributes || {};
    return attrs.ceramic_offer; // expected "1" or "applied"
  }

  function hasUpgradeLineItem(cart) {
    return (cart.items || []).some((item) => item.variant_id === UPGRADE_VARIANT_ID);
  }

  function hasWheelLineItem(cart) {
    return (cart.items || []).some((item) => {
      const p = item.properties || {};
      // Accept either naming convention
      return p._is_wheel === '1' || p.is_wheel === '1';
    });
  }

  let isRunning = false;

  async function maybeAddCeramicUpgrade() {
    if (isRunning) return;
    isRunning = true;

    try {
      const cart = await getCart();
      const offerState = getOfferState(cart);

      // Only run when offer is armed
      if (offerState !== '1') {
        if (!(STOP_AFTER_APPLIED === false && offerState === 'applied')) return;
      }

      // Must have a wheel
      if (!hasWheelLineItem(cart)) return;

      // Must not already have the upgrade
      if (hasUpgradeLineItem(cart)) return;

      // Add the upgrade
      const addResult = await addUpgradeVariant(UPGRADE_VARIANT_ID);
      if (!addResult.ok) return; // IMPORTANT: don't mark applied if add failed

      // Rebuild Impulse cart drawer UI
      document.dispatchEvent(new CustomEvent('cart:build'));

      // Mark as applied (prevents duplicate additions)
      await updateCartAttributes({ ceramic_offer: 'applied' });
    } catch (err) {
      console.error('[ceramic-offer] Unexpected error:', err);
    } finally {
      isRunning = false;
    }
  }

  // Impulse events (documented by Archetype)
  document.addEventListener('ajaxProduct:added', function () {
    maybeAddCeramicUpgrade();
  });

  // Fallbacks
  document.addEventListener('cart:updated', function () {
    maybeAddCeramicUpgrade();
  });

  document.addEventListener('DOMContentLoaded', function () {
    maybeAddCeramicUpgrade();
  });
})();
